import type {
  DealActivityDto,
  DealActivityKind,
  DealCategory,
  DealChurnReason,
  DealStage,
  DealSummaryDto,
  ListDealsResponse,
  OwnerEntity,
} from '@bhops/shared';
import { apiClient } from './client';

export interface ListDealsQuery {
  stage?: DealStage;
  category?: DealCategory;
  customerId?: string;
  salesRepCode?: string;
  search?: string;
  includeDeleted?: boolean;
  page?: number;
  pageSize?: number;
}

// §D-2 #4 (PDF p.13) — multi-model line item for a deal.
export interface DealModelLineInput {
  modelCode: string;
  quantity: number;
  notes?: string;
}

export interface CreateDealBody {
  customerId: string;
  storeId?: string;
  ownerEntity: OwnerEntity;
  salesRepCode: string;
  // BigInt over the wire as a numeric string per decisions §C-6.
  expectedRevenue?: string;
  expectedRobotCount?: number;
  expectedCloseAt?: string;
  // §D-2 (PDF p.13) — contract-prefill fields. All optional.
  // expectedUnitPrice is BigInt-as-string (KRW per robot, Q#7).
  expectedUnitPrice?: string;
  // OptionGroup `deal.contractType` (RENTAL / SALE)
  expectedContractType?: string;
  // OptionGroup `deal.contractDuration` value (months — 24, 36, …)
  expectedContractDurationMonths?: number;
  // §D-2 #4 — multi-model. Empty array OK.
  modelLines?: DealModelLineInput[];
  notes?: string;
}

export interface UpdateDealBody {
  storeId?: string | null;
  salesRepCode?: string;
  expectedRevenue?: string | null;
  expectedRobotCount?: number | null;
  expectedCloseAt?: string | null;
  expectedUnitPrice?: string | null;
  expectedContractType?: string | null;
  expectedContractDurationMonths?: number | null;
  modelLines?: DealModelLineInput[];
}

export interface TransitionDealBody {
  to: DealStage;
  churnReason?: DealChurnReason;
  churnNote?: string;
  reason?: string;
}

export interface CreateDealActivityBody {
  kind: DealActivityKind;
  occurredAt: string;
  notes: string;
}

export const dealsApi = {
  async list(query: ListDealsQuery = {}): Promise<ListDealsResponse> {
    const { data } = await apiClient.get<ListDealsResponse>('/deals', { params: query });
    return data;
  },
  async get(id: string): Promise<DealSummaryDto> {
    const { data } = await apiClient.get<DealSummaryDto>(`/deals/${id}`);
    return data;
  },
  async create(body: CreateDealBody): Promise<DealSummaryDto> {
    const { data } = await apiClient.post<DealSummaryDto>('/deals', body);
    return data;
  },
  async update(id: string, body: UpdateDealBody): Promise<DealSummaryDto> {
    const { data } = await apiClient.patch<DealSummaryDto>(`/deals/${id}`, body);
    return data;
  },
  async transition(id: string, body: TransitionDealBody): Promise<DealSummaryDto> {
    const { data } = await apiClient.post<DealSummaryDto>(`/deals/${id}/transition`, body);
    return data;
  },
  async softDelete(id: string): Promise<{ id: string; deletedAt: string | null }> {
    const { data } = await apiClient.delete<{ id: string; deletedAt: string | null }>(
      `/deals/${id}`,
    );
    return data;
  },
  // data-lifecycle plan §3 — soft-delete 복구(셀프).
  async restore(id: string): Promise<{ id: string; deletedAt: string | null }> {
    const { data } = await apiClient.post<{ id: string; deletedAt: string | null }>(
      `/deals/${encodeURIComponent(id)}/restore`,
    );
    return data;
  },
  // data-lifecycle plan §3/D3 — 셀프 완전삭제(소프트삭제된 딜만, FK 가드).
  async hardDelete(id: string): Promise<{ id: string; permanentlyDeleted: true }> {
    const { data } = await apiClient.delete<{ id: string; permanentlyDeleted: true }>(
      `/deals/${encodeURIComponent(id)}/permanent`,
    );
    return data;
  },
  async listActivities(dealId: string): Promise<DealActivityDto[]> {
    const { data } = await apiClient.get<DealActivityDto[]>(`/deals/${dealId}/activities`);
    return data;
  },
  async createActivity(dealId: string, body: CreateDealActivityBody): Promise<DealActivityDto> {
    const { data } = await apiClient.post<DealActivityDto>(`/deals/${dealId}/activities`, body);
    return data;
  },
};
